import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RefreshCw, ShieldCheck, X } from "lucide-react";
import {
  cancelAnonymousChallenge,
  completeAnonymousChallenge,
  subscribeToAnonymousChallenge,
  type AnonymousChallengeConfig,
} from "../services/AnonymousChallengeService";

interface TurnstileRenderOptions {
  sitekey: string;
  action: string;
  theme: "auto";
  size: "flexible";
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SCRIPT_ID = "dealhawk-turnstile-script";
const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let turnstileLoadPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoadPromise) return turnstileLoadPromise;

  turnstileLoadPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const onLoad = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile did not initialize."));
    };
    const onError = () => reject(new Error("Unable to load human verification."));
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    document.head.appendChild(script);
  }).catch((error) => {
    turnstileLoadPromise = null;
    document.getElementById(TURNSTILE_SCRIPT_ID)?.remove();
    throw error;
  });
  return turnstileLoadPromise;
}

export default function AnonymousChallengeHost() {
  const [challenge, setChallenge] = useState<AnonymousChallengeConfig | null>(null);
  const [error, setError] = useState("");
  const [renderAttempt, setRenderAttempt] = useState(0);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => subscribeToAnonymousChallenge(setChallenge), []);

  useEffect(() => {
    if (!challenge) return;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cancelAnonymousChallenge();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [challenge]);

  useEffect(() => {
    if (!challenge) return;
    let disposed = false;
    let widgetId = "";
    setError("");

    void loadTurnstile()
      .then((turnstile) => {
        if (disposed || !widgetContainerRef.current) return;
        widgetContainerRef.current.replaceChildren();
        widgetId = turnstile.render(widgetContainerRef.current, {
          sitekey: challenge.siteKey,
          action: challenge.action,
          theme: "auto",
          size: "flexible",
          callback: (token) => completeAnonymousChallenge(token),
          "error-callback": () => setError("Verification could not start. Please try again."),
          "expired-callback": () => setError("Verification expired. Please try again."),
        });
      })
      .catch(() => {
        if (!disposed) setError("Human verification could not load. Check your connection and try again.");
      });

    return () => {
      disposed = true;
      if (widgetId) window.turnstile?.remove(widgetId);
    };
  }, [challenge, renderAttempt]);

  if (!challenge || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/55 p-0 backdrop-blur-[3px] sm:items-center sm:p-4 sheet-backdrop-in"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) cancelAnonymousChallenge();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="anonymous-challenge-title"
        aria-describedby="anonymous-challenge-description"
        className="w-full max-w-md rounded-t-3xl border border-gray-200 bg-white px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 shadow-2xl dark:border-dark-700 dark:bg-dark-900 sm:rounded-2xl sm:p-6 sheet-panel-in-sm"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 dark:bg-primary-900/35 dark:text-primary-200">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="anonymous-challenge-title" className="text-base font-bold text-gray-950 dark:text-white">
              Quick security check
            </h2>
            <p id="anonymous-challenge-description" className="mt-1 text-sm leading-5 text-gray-600 dark:text-dark-300">
              We noticed unusually fast browsing. Complete this check once to continue.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => cancelAnonymousChallenge()}
            aria-label="Close verification"
            className="-mr-1 -mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:text-dark-300 dark:hover:bg-dark-800 dark:hover:text-white"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-5 min-h-[70px] overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-2 dark:border-dark-700 dark:bg-dark-800">
          <div ref={widgetContainerRef} className="min-h-[65px] w-full" />
        </div>

        {error ? (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:bg-red-950/35 dark:text-red-200">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setRenderAttempt((attempt) => attempt + 1)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 font-semibold hover:bg-red-100 dark:hover:bg-red-900/40"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Retry
            </button>
          </div>
        ) : null}

        <p className="mt-3 text-center text-[11px] leading-4 text-gray-400 dark:text-dark-400">
          Signed-in members are not asked to complete this check.
        </p>
      </section>
    </div>,
    document.body,
  );
}
